import { Helmet } from 'react-helmet-async';

/** Site-wide constants — update SITE_URL before deploying */
export const SITE_URL  = process.env.REACT_APP_SITE_URL || 'https://pitchandpen.com';
export const SITE_NAME = 'Pitch & Pen';
export const DEFAULT_DESC =
  'The platform where African football stories are told honestly — ' +
  'personal stories, investigations, talent spotlights, welfare & advocacy, and interviews.';
export const DEFAULT_IMAGE = `${SITE_URL}/og-default.png`;

/**
 * SEO — drop-in component for per-page meta, Open Graph, Twitter Cards and JSON-LD.
 *
 * Props
 * ─────
 * title        string   – page title (auto-appended "— Pitch & Pen")
 * description  string   – meta description (≤ 160 chars recommended)
 * image        string   – absolute OG/Twitter image URL
 * url          string   – canonical URL (defaults to current href)
 * type         string   – OG type: "website" | "article" | "profile"
 * noindex      bool     – adds noindex,nofollow (login, register, write)
 * article      object   – { author, publishedTime, modifiedTime, section, tags[] }
 * jsonLd       object   – raw JSON-LD schema to inject
 */
export default function SEO({
  title,
  description = DEFAULT_DESC,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  noindex = false,
  article,
  jsonLd,
}) {
  const fullTitle  = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — African Football Stories`;
  const canonical  = url ?? (typeof window !== 'undefined' ? window.location.href : SITE_URL);
  const metaDesc   = description.length > 160 ? description.slice(0, 157) + '…' : description;
  const absImage   = image?.startsWith('http') ? image : image ? `${SITE_URL}${image}` : DEFAULT_IMAGE;

  return (
    <Helmet>
      {/* ── Primary ── */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* ── Open Graph ── */}
      <meta property="og:site_name"   content={SITE_NAME} />
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image"       content={absImage} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url"         content={canonical} />
      <meta property="og:type"        content={type} />
      <meta property="og:locale"      content="en_GB" />

      {/* ── Article-specific OG ── */}
      {article?.author       && <meta property="article:author"         content={article.author} />}
      {article?.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
      {article?.modifiedTime  && <meta property="article:modified_time"  content={article.modifiedTime} />}
      {article?.section      && <meta property="article:section"        content={article.section} />}
      {article?.tags?.map(t  => <meta key={t} property="article:tag"    content={t} />)}

      {/* ── Twitter Card ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content="@pitchandpen" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image"       content={absImage} />

      {/* ── JSON-LD ── */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd, null, 0)}
        </script>
      )}
    </Helmet>
  );
}
